'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/local-api/client';
import PostCard from './PostCard';
import StoriesStrip from './StoriesStrip';
import ChatPanel from './ChatPanel';
import PeopleSearch from './PeopleSearch';
import PeopleYouMayKnow from './PeopleYouMayKnow';
import { FireIcon, HashtagIcon, PhotoIcon, VideoCameraIcon, XMarkIcon, GlobeAltIcon, LockClosedIcon, UsersIcon, PlusIcon, UserGroupIcon as UserGroupSolid, BellIcon, MagnifyingGlassIcon, SparklesIcon, StarIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { TagIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ChartBarIcon, ShieldExclamationIcon, ClockIcon, UserGroupIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import AdSlider from '@/components/AdSlider';
import SignInPromptModal from '@/components/SignInPromptModal';
import { useAuth } from '@/contexts/AuthContext';


const feedTabs = ['For You', 'Following', 'Trending', 'Latest'];
const feedFilters = ['All', 'General', 'Earning', 'Lost Money', 'Warning', 'Question', 'Discussion'];
const tagOptions = ['General', 'Earning', 'Lost Money', 'Warning', 'Question', 'Discussion'];
const communityCategories = ['General', 'Safety', 'Research', 'Trading', 'Education', 'Community', 'DeFi', 'Forex'];

export interface CommunityPost {
  id: string;
  author: string;
  avatar: string;
  avatarUrl?: string;
  verified: boolean;
  role: string;
  date: string;
  tag: 'General' | 'Earning' | 'Lost Money' | 'Warning' | 'Question' | 'Discussion';
  projectMention?: string;
  sourceGroupId?: string;
  content: string;
  likes: number;
  comments: number;
  shares: number;
  reactions: { fire: number; heart: number; warning: number; thumbsUp: number };
  authorId?: string;
  mediaUrls?: string[];
  mediaTypes?: string[];
  visibility?: 'community' | 'public';
  isFeatured?: boolean;
  isBoosted?: boolean;
}

interface TrendingTopic {
  id: string;
  tag: string;
  posts: number;
  trending: boolean;
}

interface MediaPreview {
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
}

interface ConnectionRequest {
  id: string;
  sender_id: string;
  receiver_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  sender_profile?: { full_name: string; role: string };
  receiver_profile?: { full_name: string; role: string };
}

interface FriendProfile {
  id: string;
  full_name: string;
  role?: string;
}

interface Community {
  id: string;
  name: string;
  description: string;
  category: string;
  creator_id: string;
  member_count: number;
  is_private: boolean;
  created_at: string;
}

interface FeaturedCommunity {
  id: string;
  community_id: string;
  status: string;
  featured_until: string | null;
  placement: string;
  communities?: Community;
}

interface PaidStory {
  id: string;
  user_id: string;
  title: string;
  content: string | null;
  image_url: string | null;
  link_url: string | null;
  cta_text: string | null;
  status: string;
  story_until: string | null;
}

interface MyActivityStats {
  myPostsCount: number;
  activeConnections: number;
  pendingRequestsSent: number;
  savedWarnings: CommunityPost[];
}

function mapPost(row: any): CommunityPost {
  const profile = row.user_profiles;
  const name = profile?.full_name || 'Community Member';
  const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  return {
    id: row.id,
    author: name,
    avatar: initials,
    avatarUrl: profile?.avatar_url || undefined,
    verified: profile?.role === 'moderator' || profile?.role === 'admin',
    role: profile?.role === 'admin' ? 'Admin' : profile?.role === 'moderator' ? 'Moderator' : 'Community Member',
    date: new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' +
      new Date(row.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    tag: row.tag,
    projectMention: row.project_mention || undefined,
    content: row.content,
    likes: row.likes ?? 0,
    comments: row.comments ?? 0,
    shares: row.shares ?? 0,
    reactions: row.reactions ?? { fire: 0, heart: 0, warning: 0, thumbsUp: 0 },
    authorId: row.author_id,
    mediaUrls: row.media_urls ?? [],
    mediaTypes: row.media_types ?? [],
    visibility: row.visibility ?? 'community',
    sourceGroupId: row.source_group_id,
    isFeatured: row.isFeatured ?? false,
  };
}

// ─── Paid Story Submit Modal ──────────────────────────────────────────────────
function PaidStoryModal({ onClose, currentUserId }: { onClose: () => void; currentUserId: string }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [ctaText, setCtaText] = useState('Learn More');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    const supabase = createClient();
    const { error } = await supabase.from('paid_stories').insert({
      user_id: currentUserId,
      title: title.trim(),
      content: content.trim() || null,
      link_url: linkUrl.trim() || null,
      cta_text: ctaText.trim() || 'Learn More',
      amount_paid: 10,
      payment_method: 'usdt',
      payment_confirmed: false,
      status: 'pending_review',
    });
    if (error) {
      toast.error('Failed to submit story');
    } else {
      toast.success('Paid story submitted! Admin will review and activate after $10 USDT payment.');
      onClose();
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <StarIcon className="text-purple-500" style={{ width: 16, height: 16 }} />
          </div>
          <h2 className="text-base font-bold text-foreground flex-1">Paid Story</h2>
          <span className="text-xs font-bold text-purple-500 bg-purple-500/10 px-2 py-0.5 rounded-full">$10 USDT</span>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors ml-1">
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-muted-foreground">Your story will appear prominently in the Stories strip for 24 hours after payment confirmation.</p>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Story Title <span className="text-red-500">*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Check out our new DeFi platform" maxLength={80} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Description</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Brief description..." rows={2} maxLength={200} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Link URL</label>
              <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." className="w-full bg-input border border-border rounded-xl px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">CTA Button</label>
              <input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Learn More" className="w-full bg-input border border-border rounded-xl px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !title.trim()} className="flex-1 py-2.5 rounded-xl bg-purple-500 text-white text-sm font-semibold hover:bg-purple-600 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
            {submitting ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <StarIcon style={{ width: 14, height: 14 }} />}
            Submit Story
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Feature Community Modal ──────────────────────────────────────────────────
function FeatureCommunityModal({ community, onClose, currentUserId }: { community: Community; onClose: () => void; currentUserId: string }) {
  const [placement, setPlacement] = useState<'sidebar' | 'feed' | 'top'>('sidebar');
  const [submitting, setSubmitting] = useState(false);

  const prices = { sidebar: 20, feed: 35, top: 50 };
  const labels = { sidebar: 'Sidebar Feature', feed: 'In-Feed Feature', top: 'Top Placement' };
  const descriptions = {
    sidebar: 'Community shown in sidebar for 7 days',
    feed: 'Community card shown in feed for 7 days',
    top: 'Community pinned at top of discover for 7 days',
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const supabase = createClient();
    const { error } = await supabase.from('featured_communities').insert({
      community_id: community.id,
      user_id: currentUserId,
      amount_paid: prices[placement],
      payment_method: 'usdt',
      payment_confirmed: false,
      status: 'pending_review',
      placement,
    });
    if (error) {
      toast.error('Failed to submit feature request');
    } else {
      toast.success(`Feature request submitted! Admin will activate after $${prices[placement]} USDT payment.`);
      onClose();
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
            <CheckBadgeIcon className="text-green-500" style={{ width: 16, height: 16 }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">Feature Community</h2>
            <p className="text-[10px] text-muted-foreground truncate">{community.name}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors">
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-muted-foreground">Feature your community to attract more members. Payment via USDT.</p>
          {(['sidebar', 'feed', 'top'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPlacement(p)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                placement === p ? 'border-green-500/60 bg-green-500/10' : 'border-border hover:border-green-500/30'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                p === 'top' ? 'bg-amber-500/20' : p === 'feed' ? 'bg-blue-500/20' : 'bg-green-500/20'
              }`}>
                <CheckBadgeIcon style={{ width: 14, height: 14 }} className={p === 'top' ? 'text-amber-500' : p === 'feed' ? 'text-blue-500' : 'text-green-500'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{labels[p]}</p>
                <p className="text-[10px] text-muted-foreground">{descriptions[p]}</p>
              </div>
              <span className="text-sm font-bold text-green-500 flex-shrink-0">${prices[p]} USDT</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
            {submitting ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CheckBadgeIcon style={{ width: 14, height: 14 }} />}
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommunityContent() {
  const [activeTab, setActiveTab] = useState('For You');
  const [filter, setFilter] = useState('All');
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [composerContent, setComposerContent] = useState('');
  const [composerTag, setComposerTag] = useState('General');
  const [composerProject, setComposerProject] = useState('');
  const [composerVisibility, setComposerVisibility] = useState<'community' | 'public'>('public');
  const [submitting, setSubmitting] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [mediaPreviews, setMediaPreviews] = useState<MediaPreview[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [incomingRequests, setIncomingRequests] = useState<ConnectionRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
  const [connections, setConnections] = useState<Set<string>>(new Set());
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [myActivity, setMyActivity] = useState<MyActivityStats>({ myPostsCount: 0, activeConnections: 0, pendingRequestsSent: 0, savedWarnings: [] });
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [friendProfiles, setFriendProfiles] = useState<FriendProfile[]>([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showPeopleSearch, setShowPeopleSearch] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [signInAction, setSignInAction] = useState('perform this action');

  const [communities, setCommunities] = useState<Community[]>([]);
  const [joinedCommunities, setJoinedCommunities] = useState<Set<string>>(new Set());
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);
  const [communityName, setCommunityName] = useState('');
  const [communityDesc, setCommunityDesc] = useState('');
  const [communityCategory, setCommunityCategory] = useState('General');
  const [communityPrivate, setCommunityPrivate] = useState(false);
  const [creatingCommunity, setCreatingCommunity] = useState(false);
  const [joiningCommunity, setJoiningCommunity] = useState<string | null>(null);
  const [communitiesExpanded, setCommunitiesExpanded] = useState(false);

  // Paid features state
  const [showPaidStoryModal, setShowPaidStoryModal] = useState(false);
  const [featureCommunity, setFeatureCommunity] = useState<Community | null>(null);
  const [featuredCommunities, setFeaturedCommunities] = useState<FeaturedCommunity[]>([]);
  const [paidStories, setPaidStories] = useState<PaidStory[]>([]);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const fetchPosts = useCallback(async () => {
    const supabase = createClient();

    // Get current user for visibility filtering
    const { data: { user: currentUserAuth } } = await supabase.auth.getUser();
    const currentUid = currentUserAuth?.id ?? null;

    // Build accepted friend IDs set for private post filtering
    let friendIds = new Set<string>();
    if (currentUid) {
      const { data: acceptedConns } = await supabase
        .from('connection_requests')
        .select('sender_id, receiver_id')
        .eq('status', 'accepted')
        .or(`sender_id.eq.${currentUid},receiver_id.eq.${currentUid}`);
      if (acceptedConns) {
        acceptedConns.forEach((r: any) => {
          if (r.sender_id === currentUid) friendIds.add(r.receiver_id);
          else friendIds.add(r.sender_id);
        });
      }
      const { data: connRows } = await supabase
        .from('connections')
        .select('user_id_1, user_id_2')
        .or(`user_id_1.eq.${currentUid},user_id_2.eq.${currentUid}`);
      if (connRows) {
        connRows.forEach((c: any) => {
          if (c.user_id_1 === currentUid) friendIds.add(c.user_id_2);
          else friendIds.add(c.user_id_1);
        });
      }
    }

    // For "Following" tab, get the list of followed user IDs first
    let followedIds: string[] = [];
    if (activeTab === 'Following') {
      if (currentUid) {
        const { data: followData } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', currentUid);
        followedIds = (followData ?? []).map((f: any) => f.following_id);
      }
    }

    let query = supabase
      .from('community_posts')
      .select('*, user_profiles(full_name, role, avatar_url)')
      .order('created_at', { ascending: false })
      .limit(50);

    if (activeTab === 'Trending') {
      query = supabase
        .from('community_posts')
        .select('*, user_profiles(full_name, role, avatar_url)')
        .order('likes', { ascending: false })
        .limit(50);
    } else if (activeTab === 'Following') {
      if (followedIds.length === 0) {
        // No follows yet — show empty state
        setPosts([]);
        setLoading(false);
        return;
      }
      query = supabase
        .from('community_posts')
        .select('*, user_profiles(full_name, role, avatar_url)')
        .in('author_id', followedIds)
        .order('created_at', { ascending: false })
        .limit(50);
    } else if (activeTab === 'Latest') {
      query = supabase
        .from('community_posts')
        .select('*, user_profiles(full_name, role, avatar_url)')
        .order('created_at', { ascending: false })
        .limit(50);
    }

    const { data, error } = await query;
    if (!error && data) {
      // Client-side visibility filter (defense-in-depth on top of DB RLS):
      // Private posts (visibility='community') are only shown to the post owner and their accepted friends.
      const visibleData = data.filter((row: any) => {
        if (row.visibility === 'public' || row.visibility == null) return true;
        // visibility === 'community' (private)
        if (!currentUid) return false; // unauthenticated users never see private posts
        if (row.author_id === currentUid) return true; // own post
        return friendIds.has(row.author_id); // friend's post
      });

      // The local MySQL API does not expand Supabase-style relationship selects,
      // so resolve post authors explicitly. Without this, every post falls back
      // to the generic "Community Member" label after deployment.
      const authorIds = Array.from(new Set(
        visibleData.map((row: any) => row.author_id).filter(Boolean)
      )) as string[];
      const profilesById = new Map<string, any>();
      if (authorIds.length > 0) {
        const { data: authorProfiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, role, avatar_url')
          .in('id', authorIds);
        (authorProfiles ?? []).forEach((profile: any) => profilesById.set(profile.id, profile));
      }
      const postsWithProfiles = visibleData.map((row: any) => ({
        ...row,
        user_profiles: profilesById.get(row.author_id) ?? row.user_profiles,
      }));

      // Fetch featured post IDs
      const supabase2 = createClient();
      const { data: featuredData } = await supabase2
        .from('featured_posts')
        .select('post_id')
        .eq('status', 'active');
      const featuredIds = new Set((featuredData || []).map((f: any) => f.post_id));

      // Fetch active boosted post IDs
      const { data: boostedData } = await supabase2
        .from('boosted_posts')
        .select('post_id')
        .eq('status', 'active')
        .eq('source_type', 'connect_post')
        .lte('boost_start_date', new Date().toISOString())
        .or(`boost_end_date.is.null,boost_end_date.gte.${new Date().toISOString()}`);
      const boostedIds = new Set((boostedData || []).map((b: any) => b.post_id));

      setPosts(postsWithProfiles.map((row: any) => ({ ...mapPost(row), isFeatured: featuredIds.has(row.id), isBoosted: boostedIds.has(row.id) })));
      const mentionCounts: Record<string, number> = {};
      visibleData.forEach((p: any) => {
        if (p.project_mention) {
          mentionCounts[p.project_mention] = (mentionCounts[p.project_mention] || 0) + 1;
        }
      });
      const topics = Object.entries(mentionCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([tag, count], i) => ({
          id: `trend-${i}`,
          tag: `#${tag.replace(/\s+/g, '')}`,
          posts: count,
          trending: count > 2,
        }));
      setTrendingTopics(topics);
    }
    setLoading(false);
  }, [activeTab]);

  const fetchCommunities = useCallback(async (userId?: string) => {
    const supabase = createClient();
    const { data } = await supabase.from('communities').select('*').order('member_count', { ascending: false }).limit(10);
    if (data) setCommunities(data as Community[]);
    if (userId) {
      const { data: memberData } = await supabase.from('community_members').select('community_id').eq('user_id', userId);
      if (memberData) setJoinedCommunities(new Set(memberData.map((m: any) => m.community_id)));
    }
    // Fetch featured communities
    const { data: featuredData } = await supabase
      .from('featured_communities')
      .select('*, communities(*)')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(3);
    if (featuredData) setFeaturedCommunities(featuredData as FeaturedCommunity[]);

    // Fetch active paid stories
    const { data: storiesData } = await supabase
      .from('paid_stories')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(5);
    if (storiesData) setPaidStories(storiesData as PaidStory[]);
  }, []);

  const fetchConnectionData = useCallback(async (userId: string) => {
    const supabase = createClient();

    // Two-step fetch: get connection_requests rows, then fetch sender profiles separately
    const { data: incomingRows } = await supabase
      .from('connection_requests')
      .select('id, sender_id, receiver_id, status, created_at')
      .eq('receiver_id', userId)
      .eq('status', 'pending');

    if (incomingRows && incomingRows.length > 0) {
      const senderIds = incomingRows.map((r: any) => r.sender_id);
      const { data: senderProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, role')
        .in('id', senderIds);
      const profileMap: Record<string, any> = {};
      (senderProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p; });
      const enriched = incomingRows.map((r: any) => ({
        ...r,
        sender_profile: profileMap[r.sender_id] ?? null,
      }));
      setIncomingRequests(enriched as ConnectionRequest[]);
    } else {
      setIncomingRequests([]);
    }

    const { data: sent } = await supabase.from('connection_requests').select('receiver_id').eq('sender_id', userId).in('status', ['pending', 'accepted']);
    if (sent) setSentRequests(new Set(sent.map((r: any) => r.receiver_id)));

    const { data: conns } = await supabase.from('connections').select('user_id_1, user_id_2').or(`user_id_1.eq.${userId},user_id_2.eq.${userId}`);
    const connSet = new Set<string>();
    if (conns && conns.length > 0) {
      conns.forEach((c: any) => {
        if (c.user_id_1 === userId) connSet.add(c.user_id_2);
        else connSet.add(c.user_id_1);
      });
    }

    // Also fetch from accepted connection_requests as fallback (in case connections table is not populated)
    const { data: acceptedReqs } = await supabase
      .from('connection_requests')
      .select('sender_id, receiver_id')
      .eq('status', 'accepted')
      .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);
    if (acceptedReqs) {
      acceptedReqs.forEach((r: any) => {
        if (r.sender_id === userId) connSet.add(r.receiver_id);
        else connSet.add(r.sender_id);
      });
    }

    setConnections(connSet);

    if (connSet.size > 0) {
      const friendIds = Array.from(connSet);
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, role')
        .in('id', friendIds);
      if (profiles) setFriendProfiles(profiles as FriendProfile[]);
    }
  }, []);

  const fetchMyActivity = useCallback(async (userId: string) => {
    const supabase = createClient();
    const { count: postsCount } = await supabase.from('community_posts').select('id', { count: 'exact', head: true }).eq('author_id', userId);
    const { count: connCount } = await supabase.from('connections').select('user_id_1', { count: 'exact', head: true }).or(`user_id_1.eq.${userId},user_id_2.eq.${userId}`);
    const { count: pendingCount } = await supabase.from('connection_requests').select('id', { count: 'exact', head: true }).eq('sender_id', userId).eq('status', 'pending');
    const { data: likedWarnings } = await supabase.from('post_likes').select('post_id, community_posts(*, user_profiles(full_name, role, avatar_url))').eq('user_id', userId).limit(20);

    const warnings: CommunityPost[] = [];
    if (likedWarnings) {
      likedWarnings.forEach((row: any) => {
        const p = row.community_posts;
        if (p && p.tag === 'Warning') warnings.push(mapPost(p));
      });
    }

    setMyActivity({
      myPostsCount: postsCount ?? 0,
      activeConnections: connCount ?? 0,
      pendingRequestsSent: pendingCount ?? 0,
      savedWarnings: warnings.slice(0, 3),
    });
  }, []);

  useEffect(() => {
    fetchPosts();
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      const user = data?.user ?? null;
      setCurrentUser(user);
      if (user) {
        fetchConnectionData(user.id);
        fetchMyActivity(user.id);
        fetchCommunities(user.id);
      } else {
        fetchCommunities();
      }
    });
    const channel = supabase
      .channel('community_posts_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'community_posts' }, () => { fetchPosts(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchPosts, fetchConnectionData, fetchMyActivity, fetchCommunities]);

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const remaining = 4 - mediaPreviews.length;
    const toAdd = files.slice(0, remaining);
    toAdd.forEach((file) => {
      const previewUrl = URL.createObjectURL(file);
      setMediaPreviews((prev) => [...prev, { file, previewUrl, type }]);
    });
    e.target.value = '';
  };

  const removeMedia = (idx: number) => {
    setMediaPreviews((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const uploadMediaFiles = async (userId: string): Promise<{ urls: string[]; types: string[] }> => {
    if (mediaPreviews.length === 0) return { urls: [], types: [] };
    const supabase = createClient();
    const urls: string[] = [];
    const types: string[] = [];
    for (let i = 0; i < mediaPreviews.length; i++) {
      const { file, type } = mediaPreviews[i];
      const ext = file.name.split('.').pop() ?? (type === 'video' ? 'mp4' : 'jpg');
      const path = `${userId}/${Date.now()}_${i}.${ext}`;
      const { data, error } = await supabase.storage.from('post-media').upload(path, file, { upsert: false });
      if (error) throw error;
      if (!data) throw new Error('Upload completed without a file path');
      const { data: urlData } = supabase.storage.from('post-media').getPublicUrl(data.path);
      urls.push(urlData.publicUrl);
      types.push(type);
      setUploadProgress(Math.round(((i + 1) / mediaPreviews.length) * 100));
    }
    return { urls, types };
  };

  const handlePost = async () => {
    if (!composerContent?.trim() || composerContent?.length < 10) {
      toast.error('Post must be at least 10 characters');
      return;
    }
    if (!currentUser) {
      toast.error('Please sign in to post');
      return;
    }
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const supabase = createClient();
      const { urls, types } = await uploadMediaFiles(currentUser.id);
      const { data: newPost, error } = await supabase.from('community_posts').insert({
        author_id: currentUser.id,
        content: composerContent,
        tag: composerTag,
        project_mention: composerProject.trim() || null,
        likes: 0,
        comments: 0,
        shares: 0,
        reactions: { fire: 0, heart: 0, warning: 0, thumbsUp: 0 },
        media_urls: urls,
        media_types: types,
        visibility: composerVisibility,
      }).select('*, user_profiles(full_name, role, avatar_url)').single();

      if (error) {
        toast.error('Failed to publish post. Please try again.');
      } else {
        setComposerContent('');
        setComposerTag('General');
        setComposerProject('');
        mediaPreviews.forEach((m) => URL.revokeObjectURL(m.previewUrl));
        setMediaPreviews([]);
        setUploadProgress(0);
        setComposerExpanded(false);
        toast.success(`Post published ${composerVisibility === 'public' ? 'publicly' : 'to friends only'}`);
        if (newPost) {
          const { data: authorProfile } = await supabase
            .from('user_profiles')
            .select('id, full_name, role, avatar_url')
            .eq('id', currentUser.id)
            .maybeSingle();
          setPosts((prev) => [mapPost({ ...newPost, user_profiles: authorProfile }), ...prev]);
        } else {
          fetchPosts();
        }
      }
    } catch {
      toast.error('An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendRequest = async (receiverId: string) => {
    if (!currentUser) { toast.error('Sign in to connect'); return; }
    if (receiverId === currentUser.id) return;
    setProcessingRequest(receiverId);
    const supabase = createClient();
    const { error } = await supabase.from('connection_requests').insert({ sender_id: currentUser.id, receiver_id: receiverId, status: 'pending' });
    if (error) {
      toast.error('Could not send request');
    } else {
      setSentRequests((prev) => new Set([...prev, receiverId]));
      toast.success('Friend request sent!');
      await supabase.from('follows').upsert({ follower_id: currentUser.id, following_id: receiverId });
    }
    setProcessingRequest(null);
  };

  const handleAcceptRequest = async (requestId: string, senderId: string) => {
    if (!currentUser) return;
    setProcessingRequest(requestId);
    const supabase = createClient();
    await supabase.from('connection_requests').update({ status: 'accepted' }).eq('id', requestId);
    const uid1 = currentUser.id < senderId ? currentUser.id : senderId;
    const uid2 = currentUser.id < senderId ? senderId : currentUser.id;
    await supabase.from('connections').upsert({ user_id_1: uid1, user_id_2: uid2 });
    await supabase.from('follows').upsert({ follower_id: currentUser.id, following_id: senderId });
    await supabase.from('follows').upsert({ follower_id: senderId, following_id: currentUser.id });
    setConnections((prev) => new Set([...prev, senderId]));
    setIncomingRequests((prev) => prev.filter((r) => r.id !== requestId));
    const { data: profile } = await supabase.from('user_profiles').select('id, full_name, role').eq('id', senderId).single();
    if (profile) setFriendProfiles((prev) => [...prev.filter((p) => p.id !== senderId), profile as FriendProfile]);
    toast.success('Connection accepted! You can now chat.');
    setProcessingRequest(null);
    setShowPendingModal(false);
  };

  const handleDeclineRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    const supabase = createClient();
    await supabase.from('connection_requests').update({ status: 'declined' }).eq('id', requestId);
    setIncomingRequests((prev) => prev.filter((r) => r.id !== requestId));
    toast.success('Request declined');
    setProcessingRequest(null);
  };

  const handleCreateCommunity = async () => {
    if (!currentUser) { toast.error('Sign in to create a community'); return; }
    if (!communityName.trim()) { toast.error('Community name is required'); return; }
    if (communityName.trim().length < 3) { toast.error('Name must be at least 3 characters'); return; }
    setCreatingCommunity(true);
    const supabase = createClient();
    const { data: newCommunity, error } = await supabase.from('communities').insert({
      name: communityName.trim(),
      description: communityDesc.trim() || null,
      category: communityCategory,
      creator_id: currentUser.id,
      is_private: communityPrivate,
      member_count: 1,
    }).select().single();

    if (error) {
      toast.error('Failed to create community');
      setCreatingCommunity(false);
      return;
    }

    await supabase.from('community_members').insert({ community_id: newCommunity.id, user_id: currentUser.id, role: 'admin' });
    setCommunities((prev) => [newCommunity as Community, ...prev]);
    setJoinedCommunities((prev) => new Set([...prev, newCommunity.id]));
    setCommunityName('');
    setCommunityDesc('');
    setCommunityCategory('General');
    setCommunityPrivate(false);
    setShowCreateCommunity(false);
    toast.success(`Community "${newCommunity.name}" created!`);
    setCreatingCommunity(false);
  };

  const handleJoinCommunity = async (communityId: string) => {
    if (!currentUser) { toast.error('Sign in to join communities'); return; }
    setJoiningCommunity(communityId);
    const supabase = createClient();
    const isJoined = joinedCommunities.has(communityId);
    if (isJoined) {
      const community = communities.find((c) => c.id === communityId);
      if (community?.creator_id === currentUser.id) {
        toast.error('Creators cannot leave their own community');
        setJoiningCommunity(null);
        return;
      }
      await supabase.from('community_members').delete().eq('community_id', communityId).eq('user_id', currentUser.id);
      setJoinedCommunities((prev) => { const s = new Set(prev); s.delete(communityId); return s; });
      setCommunities((prev) => prev.map((c) => c.id === communityId ? { ...c, member_count: Math.max(0, c.member_count - 1) } : c));
      toast.success('Left community');
    } else {
      const { error } = await supabase.from('community_members').insert({ community_id: communityId, user_id: currentUser.id, role: 'member' });
      if (!error) {
        setJoinedCommunities((prev) => new Set([...prev, communityId]));
        setCommunities((prev) => prev.map((c) => c.id === communityId ? { ...c, member_count: c.member_count + 1 } : c));
        toast.success('Joined community!');
      } else {
        toast.error('Could not join community');
      }
    }
    setJoiningCommunity(null);
  };

  const requireAuth = (action: string, callback: () => void) => {
    if (!currentUser) {
      setSignInAction(action);
      setShowSignInPrompt(true);
      return;
    }
    callback();
  };

  const filtered = posts?.filter((p) => filter === 'All' || p?.tag === filter);

  // Sort: boosted posts first (paid), then featured, then rest
  const sortedFiltered = [...filtered].sort((a, b) => {
    const aScore = a.isBoosted ? 2 : a.isFeatured ? 1 : 0;
    const bScore = b.isBoosted ? 2 : b.isFeatured ? 1 : 0;
    return bScore - aScore;
  });

  const { profile: authProfile } = useAuth();
  const composerDisplayName = authProfile?.full_name || currentUser?.user_metadata?.full_name || currentUser?.email || 'U';
  const userInitials = currentUser
    ? composerDisplayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'JD';
  const composerAvatarUrl = authProfile?.avatar_url || null;

  // Facebook-style sidebar widget for My Activity
  const MyActivityWidget = ({ mobile = false }: { mobile?: boolean }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div
        className={`flex items-center gap-2 px-4 py-3 border-b border-border ${mobile ? 'cursor-pointer' : ''}`}
        onClick={mobile ? () => setActivityExpanded((v) => !v) : undefined}
      >
        <ChartBarIcon className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
        <h3 className="text-sm font-bold text-foreground flex-1">My Activity</h3>
        {mobile && (
          activityExpanded
            ? <ChevronUpIcon className="text-muted-foreground" style={{ width: 14, height: 14 }} />
            : <ChevronDownIcon className="text-muted-foreground" style={{ width: 14, height: 14 }} />
        )}
      </div>
      {(!mobile || activityExpanded) && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: <PaperAirplaneIcon style={{ width: 14, height: 14 }} />, label: 'My Posts', value: myActivity.myPostsCount, color: 'text-[#1877f2]' },
              { icon: <UserGroupIcon style={{ width: 14, height: 14 }} />, label: 'Friends', value: myActivity.activeConnections, color: 'text-green-500' },
              { icon: <ClockIcon style={{ width: 14, height: 14 }} />, label: 'Pending', value: myActivity.pendingRequestsSent, color: 'text-amber-500' },
              { icon: <ShieldExclamationIcon style={{ width: 14, height: 14 }} />, label: 'Warnings', value: myActivity.savedWarnings.length, color: 'text-red-500' },
            ].map((item, i) => (
              <div key={i} className="bg-muted/50 rounded-xl p-3 flex flex-col gap-1">
                <div className={`flex items-center gap-1.5 ${item.color}`}>
                  {item.icon}
                  <span className="text-[10px] text-muted-foreground font-medium">{item.label}</span>
                </div>
                <span className="text-xl font-bold text-foreground">{item.value}</span>
              </div>
            ))}
          </div>
          {myActivity.savedWarnings.length === 0 && (
            <p className="text-[10px] text-muted-foreground text-center py-1">Like ⚠️ Warning posts to save them here</p>
          )}
        </div>
      )}
    </div>
  );

  // Featured Communities Widget
  const FeaturedCommunitiesWidget = () => {
    if (featuredCommunities.length === 0) return null;
    return (
      <div className="bg-gradient-to-br from-amber-500/10 via-card to-primary/10 border border-amber-500/30 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-500/20">
          <CheckBadgeIcon className="text-amber-500" style={{ width: 16, height: 16 }} />
          <h3 className="text-sm font-bold text-foreground flex-1">Featured Communities</h3>
          <span className="text-[9px] font-bold uppercase tracking-widest text-amber-500 bg-amber-500/15 px-2 py-0.5 rounded-full">Sponsored</span>
        </div>
        <div className="p-3 space-y-2">
          {featuredCommunities.map((fc) => {
            const comm = fc.communities as Community | undefined;
            if (!comm) return null;
            const isJoined = joinedCommunities.has(comm.id);
            return (
              <div key={fc.id} className="flex items-center gap-2.5 p-2 rounded-lg bg-card/60 border border-amber-500/20 hover:border-amber-500/40 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-foreground">{comm.name.slice(0, 2).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-bold text-foreground truncate">{comm.name}</p>
                    <CheckBadgeIcon style={{ width: 11, height: 11 }} className="text-amber-500 flex-shrink-0" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">{comm.member_count.toLocaleString()} members</p>
                </div>
                {currentUser && (
                  <button
                    onClick={() => handleJoinCommunity(comm.id)}
                    className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                      isJoined ? 'bg-muted text-muted-foreground' : 'bg-amber-500/20 text-amber-600 hover:bg-amber-500 hover:text-white'
                    }`}
                  >
                    {isJoined ? 'Joined' : 'Join'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Facebook-style Communities widget
  const CommunitiesWidget = ({ mobile = false }: { mobile?: boolean }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div
        className={`flex items-center gap-2 px-4 py-3 border-b border-border ${mobile ? 'cursor-pointer' : ''}`}
        onClick={mobile ? () => setCommunitiesExpanded((v) => !v) : undefined}
      >
        <UserGroupSolid className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
        <h3 className="text-sm font-bold text-foreground flex-1">Communities</h3>
        {currentUser && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowCreateCommunity(true); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2] hover:text-white transition-colors text-[10px] font-semibold"
          >
            <PlusIcon style={{ width: 11, height: 11 }} />
            Create
          </button>
        )}
        {mobile && (
          communitiesExpanded
            ? <ChevronUpIcon className="text-muted-foreground ml-1" style={{ width: 14, height: 14 }} />
            : <ChevronDownIcon className="text-muted-foreground ml-1" style={{ width: 14, height: 14 }} />
        )}
      </div>
      {(!mobile || communitiesExpanded) && (
        <div className="p-3 space-y-1">
          {communities.length === 0 ? (
            <div className="text-center py-4">
              <UserGroupSolid className="text-muted-foreground/40 mx-auto mb-2" style={{ width: 28, height: 28 }} />
              <p className="text-xs text-muted-foreground">No communities yet</p>
              {currentUser && (
                <button onClick={() => setShowCreateCommunity(true)} className="mt-2 text-xs text-[#1877f2] hover:underline font-medium">
                  Be the first to create one
                </button>
              )}
            </div>
          ) : (
            communities.slice(0, 6).map((community) => {
              const isJoined = joinedCommunities.has(community.id);
              const isProcessing = joiningCommunity === community.id;
              const isCreator = currentUser?.id === community.creator_id;
              return (
                <div key={community.id} className="flex items-center gap-2.5 py-2 px-2 rounded-lg hover:bg-muted/50 transition-colors group">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#1877f2]/30 to-purple-500/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-foreground">{community.name.slice(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-foreground truncate">{community.name}</p>
                      {isCreator && <span className="text-[9px] font-bold text-[#1877f2] bg-[#1877f2]/10 px-1 py-0.5 rounded-full flex-shrink-0">Owner</span>}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{community.member_count.toLocaleString()} members</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Feature community button for creator */}
                    {currentUser && isCreator && (
                      <button
                        onClick={() => setFeatureCommunity(community)}
                        className="p-1 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500 hover:text-white transition-colors"
                        title="Feature this community"
                      >
                        <CheckBadgeIcon style={{ width: 12, height: 12 }} />
                      </button>
                    )}
                    {currentUser && (
                      <button
                        onClick={() => handleJoinCommunity(community.id)}
                        disabled={isProcessing}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-50 ${
                          isJoined ? 'bg-muted text-muted-foreground hover:bg-red-500/10 hover:text-red-500' : 'bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2] hover:text-white'
                        }`}
                      >
                        {isProcessing ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" /> : isJoined ? 'Joined' : 'Join'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  // Friends List Widget
  const FriendsWidget = ({ mobile = false }: { mobile?: boolean }) => {
    const [friendsExpanded, setFriendsExpanded] = useState(true);
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div
          className={`flex items-center gap-2 px-4 py-3 border-b border-border ${mobile ? 'cursor-pointer' : ''}`}
          onClick={mobile ? () => setFriendsExpanded((v) => !v) : undefined}
        >
          <UsersIcon className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
          <h3 className="text-sm font-bold text-foreground flex-1">Friends</h3>
          <span className="text-[10px] font-bold text-[#1877f2] bg-[#1877f2]/10 px-2 py-0.5 rounded-full">
            {friendProfiles.length}
          </span>
          {mobile && (
            friendsExpanded
              ? <ChevronUpIcon className="text-muted-foreground ml-1" style={{ width: 14, height: 14 }} />
              : <ChevronDownIcon className="text-muted-foreground ml-1" style={{ width: 14, height: 14 }} />
          )}
        </div>
        {(!mobile || friendsExpanded) && (
          <div className="p-3 space-y-1">
            {!currentUser ? (
              <div className="text-center py-4">
                <UsersIcon className="text-muted-foreground/40 mx-auto mb-2" style={{ width: 28, height: 28 }} />
                <p className="text-xs text-muted-foreground">Sign in to see your friends</p>
              </div>
            ) : friendProfiles.length === 0 ? (
              <div className="text-center py-4">
                <UsersIcon className="text-muted-foreground/40 mx-auto mb-2" style={{ width: 28, height: 28 }} />
                <p className="text-xs text-muted-foreground">No friends yet</p>
                <button
                  onClick={() => setShowPeopleSearch(true)}
                  className="mt-2 text-xs text-[#1877f2] hover:underline font-medium"
                >
                  Find people to connect with
                </button>
              </div>
            ) : (
              friendProfiles.slice(0, 8).map((friend) => {
                const initials = friend.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={friend.id} className="flex items-center gap-2.5 py-2 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                        {initials}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-card" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">{friend.full_name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{friend.role || 'Community Member'}</p>
                    </div>
                    <button
                      onClick={() => {/* open chat */}}
                      className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2] hover:text-white transition-colors text-[10px] font-semibold"
                    >
                      Chat
                    </button>
                  </div>
                );
              })
            )}
            {currentUser && friendProfiles.length > 0 && (
              <button
                onClick={() => setShowPeopleSearch(true)}
                className="w-full mt-1 py-1.5 rounded-lg text-[11px] text-[#1877f2] hover:bg-[#1877f2]/10 font-semibold transition-colors flex items-center justify-center gap-1"
              >
                <MagnifyingGlassIcon style={{ width: 12, height: 12 }} />
                Find More People
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-4 pb-4">
      {/* Auth Gate Modal */}
      {showSignInPrompt && (
        <SignInPromptModal
          onClose={() => setShowSignInPrompt(false)}
          action={signInAction}
        />
      )}

      {/* Modals */}
      {showPeopleSearch && (
        <PeopleSearch
          currentUserId={currentUser?.id}
          onClose={() => setShowPeopleSearch(false)}
          initialSentRequests={sentRequests}
          initialConnections={connections}
          initialReceivedRequests={new Set(incomingRequests.map((r) => r.sender_id))}
          onSendRequest={(id) => setSentRequests((prev) => new Set([...prev, id]))}
        />
      )}

      {showPaidStoryModal && currentUser && (
        <PaidStoryModal
          currentUserId={currentUser.id}
          onClose={() => setShowPaidStoryModal(false)}
        />
      )}

      {featureCommunity && currentUser && (
        <FeatureCommunityModal
          community={featureCommunity}
          currentUserId={currentUser.id}
          onClose={() => setFeatureCommunity(null)}
        />
      )}

      {/* Create Community Modal */}
      {showCreateCommunity && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCreateCommunity(false)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <div className="w-8 h-8 rounded-lg bg-[#1877f2]/20 flex items-center justify-center">
                <UserGroupSolid className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
              </div>
              <h2 className="text-base font-bold text-foreground flex-1">Create Community</h2>
              <button onClick={() => setShowCreateCommunity(false)} className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors">
                <XMarkIcon style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Community Name <span className="text-red-500">*</span></label>
                <input value={communityName} onChange={(e) => setCommunityName(e.target.value)} placeholder="e.g. DeFi Safety Watchers" maxLength={60} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/50 transition-all" />
                <p className="text-[10px] text-muted-foreground mt-1">{communityName.length}/60</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Description</label>
                <textarea value={communityDesc} onChange={(e) => setCommunityDesc(e.target.value)} placeholder="What is this community about?" rows={3} maxLength={200} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/50 transition-all resize-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Category</label>
                <select value={communityCategory} onChange={(e) => setCommunityCategory(e.target.value)} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/50 transition-all">
                  {communityCategories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-foreground">Private Community</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Only invited members can join</p>
                </div>
                <button onClick={() => setCommunityPrivate((v) => !v)} className={`relative w-10 h-5 rounded-full transition-colors ${communityPrivate ? 'bg-[#1877f2]' : 'bg-muted-foreground/30'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${communityPrivate ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button onClick={() => setShowCreateCommunity(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
              <button onClick={handleCreateCommunity} disabled={creatingCommunity || !communityName.trim()} className="flex-1 py-2.5 rounded-xl bg-[#1877f2] text-white text-sm font-semibold hover:bg-[#166fe5] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
                {creatingCommunity ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <PlusIcon style={{ width: 14, height: 14 }} />}
                Create Community
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Friend Requests Modal */}
      {showPendingModal && currentUser && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPendingModal(false)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <div className="w-8 h-8 rounded-lg bg-[#1877f2]/20 flex items-center justify-center">
                <UsersIcon className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
              </div>
              <h2 className="text-base font-bold text-foreground flex-1">Friend Requests</h2>
              <span className="text-xs font-bold text-white bg-[#1877f2] px-2 py-0.5 rounded-full">{incomingRequests.length}</span>
              <button onClick={() => setShowPendingModal(false)} className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors ml-2">
                <XMarkIcon style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              {incomingRequests.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="text-muted-foreground mx-auto mb-2" style={{ width: 32, height: 32 }} />
                  <p className="text-sm text-muted-foreground">No pending friend requests</p>
                </div>
              ) : (
                incomingRequests.map((req) => {
                  const name = (req as any).sender_profile?.full_name || 'Community Member';
                  const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                  const isProcessing = processingRequest === req.id;
                  return (
                    <div key={req.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-foreground truncate">{name}</p>
                        <p className="text-xs text-muted-foreground">sent you a friend request</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <button
                          onClick={() => handleAcceptRequest(req.id, req.sender_id)}
                          disabled={isProcessing}
                          className="px-4 py-1.5 rounded-lg bg-[#1877f2] text-white text-xs font-semibold hover:bg-[#166fe5] transition-colors disabled:opacity-50 flex items-center justify-center min-w-[72px]"
                        >
                          {isProcessing ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : 'Confirm'}
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(req.id)}
                          disabled={isProcessing}
                          className="px-4 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between pt-1">
        <div>
          <h1 className="text-xl font-bold text-foreground">Connect</h1>
          <p className="text-xs text-muted-foreground mt-0.5 hidden lg:block">Share experiences · Discuss projects · Stay informed</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {currentUser && incomingRequests.length > 0 && (
            <button
              onClick={() => setShowPendingModal(true)}
              className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors text-xs font-semibold border border-amber-500/20"
            >
              <BellIcon style={{ width: 13, height: 13 }} />
              <span className="hidden sm:inline">Friend Requests</span>
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {incomingRequests.length}
              </span>
            </button>
          )}
          <button
            onClick={() => requireAuth('find and connect with people', () => setShowPeopleSearch(true))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-card border border-border text-muted-foreground hover:text-[#1877f2] hover:border-[#1877f2]/40 transition-colors text-xs font-semibold"
          >
            <MagnifyingGlassIcon style={{ width: 13, height: 13 }} />
            <span className="hidden sm:inline">Find People</span>
          </button>
          {currentUser && (
            <>
              {/* Paid Story button temporarily hidden.
              <button
                onClick={() => setShowPaidStoryModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/10 text-purple-500 hover:bg-purple-500 hover:text-white transition-colors text-xs font-semibold border border-purple-500/20"
              >
                <StarIcon style={{ width: 13, height: 13 }} />
                <span className="hidden sm:inline">Paid Story</span>
              </button>
              */}
              <button
                onClick={() => setShowCreateCommunity(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2] hover:text-white transition-colors text-xs font-semibold border border-[#1877f2]/20"
              >
                <PlusIcon style={{ width: 13, height: 13 }} />
                <span className="hidden sm:inline">New Community</span>
              </button>
            </>
          )}
          <div className="flex items-center gap-1.5 bg-card border border-border rounded-xl px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-muted-foreground">
              <span className="text-foreground font-semibold">{posts.length}</span> posts
            </span>
          </div>
        </div>
      </div>

      {/* Stories Strip */}
      <StoriesStrip currentUserId={currentUser?.id} />

      {/* Paid Stories Strip (active paid stories shown as highlighted cards) */}
      {paidStories.length > 0 && (
        <div className="flex gap-3 overflow-x-auto scrollbar-none pb-1">
          {paidStories.map((story) => (
            <a
              key={story.id}
              href={story.link_url || '#'}
              target={story.link_url ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="flex-shrink-0 w-36 rounded-2xl overflow-hidden border-2 border-purple-500/50 bg-gradient-to-b from-purple-500/20 to-card relative group hover:border-purple-500 transition-all"
            >
              <div className="absolute top-1.5 left-1.5 z-10">
                <span className="text-[8px] font-bold uppercase tracking-widest text-white bg-purple-500 px-1.5 py-0.5 rounded-full">Sponsored</span>
              </div>
              <div className="h-20 bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                <StarIcon style={{ width: 24, height: 24 }} className="text-purple-400" />
              </div>
              <div className="p-2">
                <p className="text-[10px] font-bold text-foreground leading-tight line-clamp-2">{story.title}</p>
                {story.cta_text && (
                  <span className="mt-1 inline-block text-[9px] font-semibold text-purple-500">{story.cta_text} →</span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Main layout */}
      <div className="lg:grid lg:grid-cols-3 lg:gap-5">
        {/* Feed column */}
        <div className="min-w-0 max-w-full lg:col-span-2 space-y-3">

          {/* Facebook-style Post Composer */}
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="p-3">
              <div className="flex items-start gap-2">
                {composerAvatarUrl ? (
                  <img
                    src={composerAvatarUrl}
                    alt={composerDisplayName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {userInitials}
                  </div>
                )}
                {currentUser ? (
                  <textarea
                    value={composerContent}
                    onChange={(e) => setComposerContent(e.target.value)}
                    onFocus={() => setComposerExpanded(true)}
                    onClick={() => setComposerExpanded(true)}
                    aria-label="Post content"
                    placeholder="What's on your mind?"
                    rows={1}
                    className={`flex-1 min-w-0 bg-muted border border-border px-4 py-2.5 text-sm leading-5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/50 resize-none transition-all duration-200 ${composerExpanded ? 'h-32 rounded-2xl overflow-y-auto' : 'h-11 rounded-full overflow-hidden'}`}
                  />
                ) : (
                  <button
                    onClick={() => requireAuth('post to the community', () => setComposerExpanded(true))}
                    className="flex-1 bg-muted hover:bg-muted/80 rounded-full px-4 py-2.5 text-sm text-muted-foreground text-left transition-colors border border-border"
                  >
                    {currentUser ? "What's on your mind?" : "Sign in to post..."}
                  </button>
                )}
              </div>

              {composerExpanded && currentUser && (
                <div className="mt-3 space-y-3">
                  {mediaPreviews.length > 0 && (
                    <div className={`grid gap-2 ${mediaPreviews.length === 1 ? 'grid-cols-1' : mediaPreviews.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      {mediaPreviews.map((m, i) => (
                        <div key={i} className="relative rounded-xl overflow-hidden bg-muted aspect-square group">
                          {m.type === 'video' ? (
                            <video src={m.previewUrl} className="w-full h-full object-cover" muted preload="metadata" />
                          ) : (
                            <img src={m.previewUrl} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                          )}
                          <button onClick={() => removeMedia(i)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/90 transition-colors">
                            <XMarkIcon style={{ width: 12, height: 12 }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {submitting && uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="space-y-1">
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-[#1877f2] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                       <button
                        onClick={() => setComposerVisibility('public')}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${composerVisibility === 'public' ? 'bg-green-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        <GlobeAltIcon style={{ width: 11, height: 11 }} />
                        Public
                      </button>
                      <button
                        onClick={() => setComposerVisibility('community')}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${composerVisibility === 'community' ? 'bg-[#1877f2] text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        <LockClosedIcon style={{ width: 11, height: 11 }} />
                        Friends only
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <TagIcon className="text-muted-foreground" style={{ width: 13, height: 13 }} />
                      <select value={composerTag} onChange={(e) => setComposerTag(e.target.value)} className="bg-input border border-border rounded-lg px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[#1877f2]">
                        {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <input value={composerProject} onChange={(e) => setComposerProject(e.target.value)} placeholder="Mention a project (optional)" className="flex-1 min-w-28 bg-input border border-border rounded-lg px-3 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#1877f2]" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 px-3 py-2 border-t border-border">
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" multiple className="hidden" onChange={(e) => handleMediaSelect(e, 'image')} />
              <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" onChange={(e) => handleMediaSelect(e, 'video')} />

              <button
                onClick={() => requireAuth('post photos or videos', () => { setComposerExpanded(true); photoInputRef.current?.click(); })}
                disabled={mediaPreviews.length >= 4}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-green-500 transition-colors text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PhotoIcon style={{ width: 18, height: 18 }} />
                <span className="hidden sm:inline">Photo/Video</span>
              </button>
              <button
                onClick={() => requireAuth('post videos', () => { setComposerExpanded(true); videoInputRef.current?.click(); })}
                disabled={mediaPreviews.length >= 4}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-red-500 transition-colors text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed sm:hidden"
              >
                <VideoCameraIcon style={{ width: 18, height: 18 }} />
              </button>
              <button
                onClick={() => requireAuth('tag a post', () => setComposerExpanded(true))}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-amber-500 transition-colors text-xs font-semibold"
              >
                <TagIcon style={{ width: 18, height: 18 }} />
                <span className="hidden sm:inline">Tag</span>
              </button>

              {/* Paid Story entry in composer temporarily hidden.
              {currentUser && (
                <button
                  onClick={() => setShowPaidStoryModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-purple-500 transition-colors text-xs font-semibold"
                >
                  <SparklesIcon style={{ width: 18, height: 18 }} />
                  <span className="hidden sm:inline">Boost</span>
                </button>
              )}
              */}

              {composerExpanded && currentUser && (
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => { setComposerExpanded(false); setComposerContent(''); setMediaPreviews([]); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handlePost}
                    disabled={submitting || !composerContent?.trim()}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#1877f2] text-white text-xs font-semibold hover:bg-[#166fe5] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all duration-150"
                  >
                    {submitting ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <PaperAirplaneIcon style={{ width: 13, height: 13 }} />}
                    Post
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Feed Filters */}
          <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
            {feedFilters?.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  filter === f ? 'bg-[#1877f2] text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-[#1877f2]/40'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Community Feed Ad — top of feed */}
          <AdSlider placementSlot="community_feed" variant="compact" />

          {/* Posts Feed */}
          <div className="space-y-3">
            {loading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="p-4 animate-pulse border-b border-border/40 last:border-b-0">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/3" />
                      <div className="h-3 bg-muted rounded w-full" />
                      <div className="h-3 bg-muted rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))
            ) : sortedFiltered?.length === 0 ? (
              <div className="p-10 flex flex-col items-center gap-3 text-center">
                <p className="text-sm font-semibold text-foreground">No posts yet</p>
                <p className="text-muted-foreground text-xs">Be the first to share with the community</p>
              </div>
            ) : (
              sortedFiltered?.map((post, idx) => (
                <React.Fragment key={post?.id}>
                  {/* Featured post badge */}
                  {post.isFeatured && (
                    <div className="flex items-center gap-1.5 px-4 pt-2 pb-0 bg-card border-x border-t border-border rounded-t-xl -mb-3">
                      <SparklesIcon style={{ width: 12, height: 12 }} className="text-amber-500" />
                      <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Featured Post</span>
                    </div>
                  )}
                  <div className={post.isFeatured ? 'border-l-2 border-amber-500/50 rounded-xl overflow-hidden' : ''}>
                    <PostCard
                      post={post}
                      currentUserId={currentUser?.id}
                      sentRequests={sentRequests}
                      connections={connections}
                      onSendRequest={handleSendRequest}
                      processingRequest={processingRequest}
                    />
                  </div>
                  {currentUser && sortedFiltered.length > 1 && idx === Math.min(2, Math.floor(sortedFiltered.length / 2) - 1) && (
                    <PeopleYouMayKnow
                      currentUserId={currentUser.id}
                      connections={connections}
                      sentRequests={sentRequests}
                      incomingRequests={incomingRequests}
                      processingRequest={processingRequest}
                      onSendRequest={handleSendRequest}
                      onSeeAll={() => setShowPeopleSearch(true)}
                    />
                  )}
                  {/* Mid-feed ad every 5 posts */}
                  {idx > 0 && idx % 5 === 0 && (
                    <div className="px-2 py-1">
                      <AdSlider placementSlot="community_feed_inline" variant="compact" />
                    </div>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Right Sidebar — desktop only */}
        <div className="hidden lg:block space-y-4">
          {/* My Activity */}
          {currentUser && <MyActivityWidget />}

          {/* Friends List */}
          <FriendsWidget />

          {/* Featured Communities — prominent placement */}
          <FeaturedCommunitiesWidget />

          {/* Pending Friend Requests */}
          {currentUser && incomingRequests.length > 0 && (
            <div className="bg-card border border-amber-500/30 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-500/20 bg-amber-500/5">
                <BellIcon className="text-amber-500" style={{ width: 16, height: 16 }} />
                <h3 className="text-sm font-bold text-foreground flex-1">Friend Requests</h3>
                <span className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full">
                  {incomingRequests.length} new
                </span>
              </div>
              <div className="p-3 space-y-3">
                {incomingRequests.slice(0, 3).map((req) => {
                  const name = (req as any).sender_profile?.full_name || 'Community Member';
                  const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                  const isProcessing = processingRequest === req.id;
                  return (
                    <div key={req.id} className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-foreground truncate">{name}</p>
                        <p className="text-[10px] text-muted-foreground">sent you a friend request</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleAcceptRequest(req.id, req.sender_id)}
                          disabled={isProcessing}
                          className="px-3 py-1 rounded-lg bg-[#1877f2] text-white text-[10px] font-semibold hover:bg-[#166fe5] transition-colors disabled:opacity-50"
                        >
                          {isProcessing ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin inline-block" /> : 'Confirm'}
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(req.id)}
                          disabled={isProcessing}
                          className="px-3 py-1 rounded-lg bg-muted text-muted-foreground text-[10px] font-semibold hover:bg-muted/80 transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
                {incomingRequests.length > 3 && (
                  <button onClick={() => setShowPendingModal(true)} className="w-full text-xs text-[#1877f2] hover:underline font-medium text-center py-1">
                    View all {incomingRequests.length} requests
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Communities */}
          <CommunitiesWidget />

          {/* Trending Topics */}
          {trendingTopics.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <FireIcon className="text-orange-400" style={{ width: 16, height: 16 }} />
                <h3 className="text-sm font-bold text-foreground">Trending Topics</h3>
              </div>
              <div className="space-y-1">
                {trendingTopics?.slice(0, 5)?.map((topic) => (
                  <div key={topic?.id} className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors">
                    <div className="flex items-center gap-2">
                      <HashtagIcon className="text-muted-foreground" style={{ width: 12, height: 12 }} />
                      <span className="text-sm text-foreground font-medium">{topic?.tag?.replace('#', '')}</span>
                      {topic?.trending && <span className="text-[9px] font-bold text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded-full">HOT</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{topic?.posts} posts</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sidebar community ad */}
          <AdSlider placementSlot="community_sidebar" variant="compact" />
        </div>
      </div>

      {/* Mobile: Friends List */}
      <div className="lg:hidden">
        <FriendsWidget mobile />
      </div>

      {/* Mobile: Featured Communities */}
      <div className="lg:hidden">
        <FeaturedCommunitiesWidget />
      </div>

      {/* Mobile: Communities */}
      <div className="lg:hidden">
        <CommunitiesWidget mobile />
      </div>

      {/* Mobile: Friend Requests */}
      {currentUser && incomingRequests.length > 0 && (
        <div className="bg-card border border-amber-500/30 rounded-xl p-4 lg:hidden">
          <div className="flex items-center gap-2 mb-3">
            <BellIcon className="text-amber-500" style={{ width: 16, height: 16 }} />
            <h3 className="text-sm font-bold text-foreground">Friend Requests</h3>
            <span className="ml-auto text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full">{incomingRequests.length} new</span>
          </div>
          <div className="space-y-3">
            {incomingRequests.slice(0, 2).map((req) => {
              const name = (req as any).sender_profile?.full_name || 'Community Member';
              const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
              const isProcessing = processingRequest === req.id;
              return (
                <div key={req.id} className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{initials}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground truncate">{name}</p>
                    <p className="text-[10px] text-muted-foreground">sent you a friend request</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleAcceptRequest(req.id, req.sender_id)} disabled={isProcessing} className="px-2.5 py-1 rounded-lg bg-[#1877f2] text-white text-[10px] font-semibold hover:bg-[#166fe5] transition-colors disabled:opacity-50">
                      {isProcessing ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin inline-block" /> : 'Confirm'}
                    </button>
                    <button onClick={() => handleDeclineRequest(req.id)} disabled={isProcessing} className="px-2.5 py-1 rounded-lg bg-muted text-muted-foreground text-[10px] font-semibold hover:bg-muted/80 transition-colors disabled:opacity-50">Delete</button>
                  </div>
                </div>
              );
            })}
            {incomingRequests.length > 2 && (
              <button onClick={() => setShowPendingModal(true)} className="w-full text-xs text-[#1877f2] hover:underline font-medium text-center py-1">
                View all {incomingRequests.length} requests
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile: My Activity */}
      {currentUser && (
        <div className="lg:hidden">
          <MyActivityWidget mobile />
        </div>
      )}

      {/* Mobile: Trending */}
      {trendingTopics.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4 lg:hidden">
          <div className="flex items-center gap-2 mb-3">
            <FireIcon className="text-orange-400" style={{ width: 16, height: 16 }} />
            <h3 className="text-sm font-bold text-foreground">Trending Topics</h3>
          </div>
          <div className="space-y-1">
            {trendingTopics?.slice(0, 5)?.map((topic) => (
              <div key={topic?.id} className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors">
                <div className="flex items-center gap-2">
                  <HashtagIcon className="text-muted-foreground" style={{ width: 12, height: 12 }} />
                  <span className="text-sm text-foreground font-medium">{topic?.tag?.replace('#', '')}</span>
                  {topic?.trending && <span className="text-[9px] font-bold text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded-full">HOT</span>}
                </div>
                <span className="text-xs text-muted-foreground">{topic?.posts} posts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {currentUser && (
        <ChatPanel currentUserId={currentUser.id} friends={friendProfiles} />
      )}
    </div>
  );
}
